import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import {
  Users,
  FileText,
  Clock,
  TrendingUp,
  DollarSign,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Briefcase,
  MessageSquare,
  FolderOpen,
} from "lucide-react";
import { toast } from "sonner";

const AdminAnalytics = () => {
  const { user, token, API } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch analytics");
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getCategoryColor = (category) => {
    const colors = {
      discovery: "bg-purple-500",
      planning: "bg-blue-500",
      implementation: "bg-green-500",
      training: "bg-yellow-500",
      review: "bg-orange-500",
      compliance: "bg-red-500",
      support: "bg-teal-500",
      documentation: "bg-indigo-500",
      meetings: "bg-pink-500",
      research: "bg-cyan-500",
      other: "bg-gray-500",
    };
    return colors[category] || colors.other;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      discovery: "Discovery",
      planning: "Planning",
      implementation: "Implementation",
      training: "Training",
      review: "Review",
      compliance: "Compliance",
      support: "Support",
      documentation: "Documentation",
      meetings: "Meetings",
      research: "Research",
      other: "Other",
    };
    return labels[category] || category;
  };

  return (
    <AdminLayout>
      <div className="max-w-[1200px] mx-auto animate-fade-in">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-rnb-navy tracking-tight">
            Analytics Dashboard
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Comprehensive overview of business performance and metrics.
          </p>
        </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rnb-navy"></div>
              </div>
            ) : analytics ? (
              <div className="space-y-8">
                {/* Revenue Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card data-testid="total-revenue">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                          <DollarSign className="h-6 w-6 text-green-600" />
                        </div>
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                          Revenue
                        </span>
                      </div>
                      <p className="text-sm text-rnb-gray mb-1">Total Revenue</p>
                      <p className="font-mono-numbers text-3xl font-bold text-rnb-navy">
                        {formatCurrency(analytics.revenue.total_revenue)}
                      </p>
                    </CardContent>
                  </Card>

                  <Card data-testid="outstanding-amount">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                          <FileText className="h-6 w-6 text-orange-600" />
                        </div>
                        <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                          Pending
                        </span>
                      </div>
                      <p className="text-sm text-rnb-gray mb-1">Outstanding</p>
                      <p className="font-mono-numbers text-3xl font-bold text-rnb-navy">
                        {formatCurrency(analytics.revenue.outstanding_amount)}
                      </p>
                    </CardContent>
                  </Card>

                  <Card data-testid="total-clients">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Users className="h-6 w-6 text-blue-600" />
                        </div>
                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                          Clients
                        </span>
                      </div>
                      <p className="text-sm text-rnb-gray mb-1">Total Clients</p>
                      <p className="font-mono-numbers text-3xl font-bold text-rnb-navy">
                        {analytics.clients.total_clients}
                      </p>
                      <p className="text-xs text-rnb-gray mt-1">
                        {analytics.clients.active_clients_30d} active (30d)
                      </p>
                    </CardContent>
                  </Card>

                  <Card data-testid="total-hours">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                          <Clock className="h-6 w-6 text-purple-600" />
                        </div>
                        <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                          Hours
                        </span>
                      </div>
                      <p className="text-sm text-rnb-gray mb-1">Total Hours</p>
                      <p className="font-mono-numbers text-3xl font-bold text-rnb-navy">
                        {analytics.utilization.total_hours.toFixed(1)}
                      </p>
                      <p className="text-xs text-rnb-gray mt-1">
                        {analytics.utilization.hours_30d.toFixed(1)} hrs (30d)
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Invoice Stats & Program Performance */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card data-testid="invoice-stats">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-rnb-blue" />
                        Invoice Statistics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <span className="text-rnb-gray">Total Invoices</span>
                          <span className="font-mono-numbers font-bold text-rnb-navy">
                            {analytics.revenue.invoices.total}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                          <span className="text-green-700">Paid</span>
                          <span className="font-mono-numbers font-bold text-green-700">
                            {analytics.revenue.invoices.paid}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                          <span className="text-orange-700">Pending</span>
                          <span className="font-mono-numbers font-bold text-orange-700">
                            {analytics.revenue.invoices.pending}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card data-testid="program-performance">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-rnb-blue" />
                        Program Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <span className="text-rnb-gray">Total Applications</span>
                          <span className="font-mono-numbers font-bold text-rnb-navy">
                            {analytics.programs.total_applications}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                          <span className="text-blue-700">Conversion Rate</span>
                          <span className="font-mono-numbers font-bold text-blue-700">
                            {analytics.programs.conversion_rate}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                          <span className="text-green-700">Program Revenue</span>
                          <span className="font-mono-numbers font-bold text-green-700">
                            {formatCurrency(analytics.revenue.program_revenue)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Hours by Category & Consultant */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card data-testid="hours-by-category">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PieChart className="h-5 w-5 text-rnb-blue" />
                        Hours by Category
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {Object.keys(analytics.utilization.hours_by_category).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(analytics.utilization.hours_by_category)
                            .sort(([,a], [,b]) => b - a)
                            .map(([category, hours]) => (
                              <div key={category} className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${getCategoryColor(category)}`} />
                                <span className="flex-1 text-sm text-rnb-gray">
                                  {getCategoryLabel(category)}
                                </span>
                                <span className="font-mono-numbers font-medium text-rnb-navy">
                                  {hours.toFixed(1)} hrs
                                </span>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <p className="text-center text-rnb-gray py-4">No data available</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card data-testid="hours-by-consultant">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-rnb-blue" />
                        Hours by Consultant
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {Object.keys(analytics.utilization.hours_by_consultant).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(analytics.utilization.hours_by_consultant)
                            .sort(([,a], [,b]) => b - a)
                            .map(([consultant, hours]) => (
                              <div key={consultant} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <span className="text-sm text-rnb-navy font-medium">
                                  {consultant}
                                </span>
                                <span className="font-mono-numbers font-bold text-rnb-blue">
                                  {hours.toFixed(1)} hrs
                                </span>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <p className="text-center text-rnb-gray py-4">No data available</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Client Engagement & Quote Stats */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card data-testid="client-engagement">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-rnb-blue" />
                        Client Engagement (30 Days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-slate-50 rounded-lg">
                          <MessageSquare className="h-6 w-6 text-rnb-blue mx-auto mb-2" />
                          <p className="font-mono-numbers text-2xl font-bold text-rnb-navy">
                            {analytics.clients.messages_30d}
                          </p>
                          <p className="text-xs text-rnb-gray">Messages</p>
                        </div>
                        <div className="text-center p-4 bg-slate-50 rounded-lg">
                          <FolderOpen className="h-6 w-6 text-rnb-blue mx-auto mb-2" />
                          <p className="font-mono-numbers text-2xl font-bold text-rnb-navy">
                            {analytics.clients.files_30d}
                          </p>
                          <p className="text-xs text-rnb-gray">Files</p>
                        </div>
                        <div className="text-center p-4 bg-slate-50 rounded-lg">
                          <Users className="h-6 w-6 text-rnb-blue mx-auto mb-2" />
                          <p className="font-mono-numbers text-2xl font-bold text-rnb-navy">
                            {analytics.clients.active_clients_30d}
                          </p>
                          <p className="text-xs text-rnb-gray">Active</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card data-testid="quote-stats">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-rnb-blue" />
                        Quote Analytics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <span className="text-rnb-gray">Total Quotes</span>
                          <span className="font-mono-numbers font-bold text-rnb-navy">
                            {analytics.quotes.total_quotes}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <span className="text-rnb-gray">Recent (30d)</span>
                          <span className="font-mono-numbers font-bold text-rnb-navy">
                            {analytics.quotes.recent_quotes_30d}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                          <span className="text-blue-700">Total Quote Value</span>
                          <span className="font-mono-numbers font-bold text-blue-700">
                            {formatCurrency(analytics.quotes.total_quote_value)}/mo
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-rnb-gray">No analytics data available.</p>
              </div>
            )}
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;
